import styled from "styled-components";
import { Toolbar, ToolbarButton } from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowUndo20Regular,
  Delete20Regular,
  Edit20Regular,
  FilterDismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import React, { useContext, useMemo } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";
import { ViewDialogState } from "./ColumnOptionsPanel";
import { isColumnFiltered } from "./utils";

type Props = {
  setViewDialog: (viewDialogState: ViewDialogState) => void;
};

export const ViewToolbar = ({ setViewDialog }: Props) => {
  const {
    selectedView,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    columns,
    strings,
    hideViewOptionsFeatures,
    disableNewView,
    disableButtons,
    hasUnsavedChanges,
    id,
  } = useContext(ColumnOptionsContext);

  const areAnyFiltersSelected = useMemo(
    () => columns.some((c) => isColumnFiltered(c.filter)),
    [columns]
  );

  return (
    <StyledToolbar aria-label={strings.headerLabel}>
      {!hideViewOptionsFeatures && (
        <ToolbarButton
          icon={<Add20Regular />}
          disabled={!!disableNewView || disableButtons?.new}
          onClick={() => setViewDialog({ isOpen: true, userAppSettingId: null })}
          id={id ? `${id}-btn-new` : undefined}
          data-testid="column-options-toolbar-new"
        >
          {strings.newViewButtonLabel}
        </ToolbarButton>
      )}
      {!hideViewOptionsFeatures && (
        <ToolbarButton
          icon={<Save20Regular />}
          disabled={disableButtons?.save || !selectedView}
          onClick={onClickSaveView}
          className={hasUnsavedChanges ? "save-dirty" : undefined}
          id={id ? `${id}-btn-save` : undefined}
          data-testid="column-options-toolbar-save"
        >
          {strings.saveViewButtonLabel}
        </ToolbarButton>
      )}
      {!hideViewOptionsFeatures && (
        <ToolbarButton
          icon={<Edit20Regular />}
          disabled={disableButtons?.edit || !selectedView}
          onClick={() => {
            if (!selectedView) return;
            setViewDialog({
              isOpen: true,
              userAppSettingId: selectedView.userAppSettingId,
            });
          }}
          id={id ? `${id}-btn-edit` : undefined}
          data-testid="column-options-toolbar-edit"
        >
          {strings.editViewButtonLabel}
        </ToolbarButton>
      )}
      <ToolbarButton
        icon={<ArrowUndo20Regular />}
        disabled={disableButtons?.reset}
        onClick={onClickReset}
        id={id ? `${id}-btn-reset` : undefined}
        data-testid="column-options-toolbar-reset"
      >
        {strings.resetButtonLabel}
      </ToolbarButton>
      <ToolbarButton
        icon={<FilterDismiss20Regular />}
        disabled={disableButtons?.clearAllFilters || !areAnyFiltersSelected}
        onClick={onClickClearAllFilters}
        id={id ? `${id}-btn-clear-filters` : undefined}
        data-testid="column-options-toolbar-clear-filters"
      >
        {strings.clearFiltersButtonLabel}
      </ToolbarButton>
      {!hideViewOptionsFeatures && (
        <ToolbarButton
          icon={<Delete20Regular />}
          disabled={disableButtons?.delete || !selectedView}
          onClick={onClickDeleteView}
          id={id ? `${id}-btn-delete` : undefined}
          data-testid="column-options-toolbar-delete"
        >
          {strings.deleteViewButtonLabel}
        </ToolbarButton>
      )}
    </StyledToolbar>
  );
};

const StyledToolbar = styled(Toolbar)`
  flex-wrap: wrap;
  padding: 0;

  .save-dirty {
    background-color: ${(props) => props.theme.palette.themeLighterAlt};
  }

  .save-dirty:not(:disabled) {
    color: ${(props) => props.theme.palette.themePrimary};
    font-weight: 700;
  }
`;
